import {
  API_TYPES,
  type GetWorkerRequirementsResponse,
  WorkerRequirementStatus,
} from "@clipboard-health/contract-worker-app-bff";
import { type UseInfiniteGetQueryOptions } from "@src/appV2/api/useInfiniteGetQuery";
import { useFilterBackgroundCheckRequirements } from "@src/appV2/redesign/BackgroundChecks/useFilterBackgroundCheckRequirements";
import { type AxiosError } from "axios";
import { minutesToMilliseconds } from "date-fns";
import { useMemo } from "react";

import { type WorkerRequirement } from "../api/types";
import { useGetPaginatedWorkerRequirements } from "../api/useGetPaginatedWorkerRequirements";
import { useGetWorkerLicensesAllowedForDocuments } from "../useGetWorkerLicensesAllowedForDocuments";

const MIN_SEARCH_LENGTH = 1;
const PAGE_SIZE = 20;
export const SEARCH_WORKER_REQUIREMENTS_STALE_TIME = minutesToMilliseconds(1);

interface UseSearchWorkerRequirementsParams {
  searchTerm: string;
}

type UseSearchWorkerRequirementsOptions = Pick<
  UseInfiniteGetQueryOptions<GetWorkerRequirementsResponse, AxiosError, WorkerRequirement[]>,
  "enabled"
>;

export function useSearchWorkerRequirements(
  params: UseSearchWorkerRequirementsParams,
  options: UseSearchWorkerRequirementsOptions = {}
) {
  const { searchTerm } = params;
  const { enabled = true } = options;

  const { licenses: qualifications } = useGetWorkerLicensesAllowedForDocuments();
  const { filterOutBackgroundCheckRequirements } = useFilterBackgroundCheckRequirements();

  const query = useGetPaginatedWorkerRequirements(
    {
      filter: {
        qualifications,
        visibleToWorker: true,
        requiredForBooking: "",
        ...(searchTerm.length >= MIN_SEARCH_LENGTH && {
          "requirement.name": searchTerm,
        }),
      },
      include: [API_TYPES.requirements, API_TYPES.requirementMappings],
      page: { size: PAGE_SIZE },
      sort: ["-requiredForBooking", "level", "-requiredByWorkplacesCount"],
    },
    { enabled, staleTime: SEARCH_WORKER_REQUIREMENTS_STALE_TIME }
  );

  const results = useMemo(() => {
    const flatResults = query.data?.pages.flat() ?? [];

    return filterOutBackgroundCheckRequirements({
      workerRequirements: flatResults,
      filter: {
        status: [WorkerRequirementStatus.MISSING, WorkerRequirementStatus.EXPIRED],
      },
    });
  }, [query.data?.pages, filterOutBackgroundCheckRequirements]);

  return {
    ...query,
    results,
  };
}
